import React, { useState, useRef } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  RefreshCw, 
  Star, 
  Clock, 
  FileText,
  MessageSquare,
  ChevronLeft,
  ChevronRight 
} from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { format, formatDistanceToNow } from 'date-fns';
import type { ExecutionLog } from '../../types/agent-types-new';
import EnhancedLogDetailDialog from './EnhancedLogDetailDialog';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { agentService } from '../../lib/api/agent-service';


interface EnhancedExecutionLogsTableProps {
  logs: ExecutionLog[];
  onRefresh: () => void;
  loading?: boolean;
  agentTypes?: string[];
  /** The real error from the logs fetch, rendered above the table. */
  error?: string | null;
}

const EnhancedExecutionLogsTable: React.FC<EnhancedExecutionLogsTableProps> = ({ 
  logs, 
  onRefresh, 
  loading = false,
  agentTypes = [],
  error = null
}) => {
  const [selectedLog, setSelectedLog] = useState<ExecutionLog | null>(null);
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Each click gets a sequence number; a response is applied only if it is still the
  // current one, so a slow request for row A cannot overwrite row B or clear its spinner.
  const detailRequestSeq = useRef(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterAgentType, setFilterAgentType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const logsPerPage = 10;

  // Only rows the API returned. The playground's local copies (localStorage testLogs:
  // client clock, invented trace id, status 'success' regardless, zero metrics) are gone:
  // a playground run POSTs to /api/agents-test/test-run, which writes the real agent_logs
  // row, and that row is what this table shows.
  // Status values come from the rows themselves (agent_logs.status), not from a fixed list.
  const statuses = Array.from(new Set(logs.map((l) => l.status).filter(Boolean))).sort();
  
  // Filter logs based on current filters
  const filteredLogs = logs.filter(log => {
    // Agent type filter
    if (filterAgentType !== 'all' && log.agent_type !== filterAgentType) {
      return false;
    }
    
    // Status filter
    if (filterStatus !== 'all' && log.status !== filterStatus) {
      return false;
    }
    
    // Search query (across multiple fields)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTraceId = log.traceId?.toLowerCase().includes(query);
      const matchesAgentType = log.agent_type?.toLowerCase().includes(query);
      const matchesInputType = log.inputType?.toLowerCase().includes(query);
      const matchesCustomerMessage = log.input?.customerMessage?.toLowerCase().includes(query);
      
      return matchesTraceId || matchesAgentType || matchesInputType || matchesCustomerMessage;
    }
    
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Render the row's own status word; colour the ones we know, never relabel one we don't.
  const getStatusBadge = (status: string) => {
    const value = status || 'unknown';
    switch (value) {
      case 'success':
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">{value}</Badge>;
      case 'failed':
      case 'error':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-200">{value}</Badge>;
      case 'timeout':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">{value}</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">{value}</Badge>;
      default:
        return <Badge variant="outline">{value}</Badge>;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return { formatted: 'not recorded', relative: '' };
    try {
      const date = new Date(timestamp);
      return {
        formatted: format(date, 'MMM d, yyyy h:mm a'),
        relative: formatDistanceToNow(date, { addSuffix: true })
      };
    } catch (error) {
      return {
        formatted: timestamp,
        relative: 'Unknown'
      };
    }
  };

  /**
   * Open the detail dialog for a row and fetch the full log through agentService.
   * A failure is shown as the failure; there is no local fallback chain, no bypass
   * endpoint and no isMock object (the previous version tried three raw fetches and
   * then built a mock from the row).
   */
  // The identifier kind is explicit: a trace id goes to the by-trace route (it is the
  // backend key on every agent_logs row, and a playground row carries a local
  // test-<timestamp> id beside its real trace id); only a row without a trace id is
  // fetched by its row uuid.
  const fetchDetail = (row: ExecutionLog) =>
    row.traceId ? agentService.getLogByTraceId(row.traceId) : agentService.getLogById(row.id);

  const handleLogClick = async (row: ExecutionLog) => {
    const seq = ++detailRequestSeq.current;
    setDetailError(null);
    setSelectedLog(null);
    setIsDetailOpen(true);
    if (!row.traceId && !row.id) {
      setDetailError('This row carries no trace id and no row id, so its detail cannot be fetched.');
      return;
    }
    setIsLogLoading(true);
    try {
      const detail = await fetchDetail(row);
      if (seq !== detailRequestSeq.current) return; // superseded by a newer click or a close
      setSelectedLog({ ...detail, originalRowData: row });
    } catch (err) {
      if (seq !== detailRequestSeq.current) return;
      setDetailError(err instanceof Error ? err.message : String(err));
    } finally {
      if (seq === detailRequestSeq.current) setIsLogLoading(false);
    }
  };

  const closeDetail = () => {
    detailRequestSeq.current += 1; // invalidate any request still in flight
    setIsDetailOpen(false);
    setSelectedLog(null);
    setDetailError(null);
    setIsLogLoading(false);
  };

  return (
    <>
      <Card className="bg-white border shadow-sm">
        <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="font-medium">Execution Logs</h3>
            <p className="text-sm text-muted-foreground">
              Showing {filteredLogs.length} of {logs.length} logs
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        {error && (
          <div className="p-4 border-b text-sm text-red-700 bg-red-50" role="alert">
            Logs could not be loaded: {error}
          </div>
        )}

        <div className="p-4 border-b">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="w-full"
              />
            </div>
            
            <div className="flex gap-2">
              <Select 
                value={filterAgentType} 
                onValueChange={(value) => {
                  setFilterAgentType(value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Agent Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agentTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={filterStatus} 
                onValueChange={(value) => {
                  setFilterStatus(value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statuses.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Agent Type</TableHead>
                  <TableHead>Input Type</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Feedback</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentLogs.length > 0 ? (
                  currentLogs.map((log) => {
                    const time = formatTimestamp(log.timestamp);
                    const rowClassName = 'cursor-pointer hover:bg-muted/50';
                    
                    return (
                      <TableRow 
                        key={log.id || `log-${time.formatted}`} 
                        onClick={() => handleLogClick(log)}
                        className={rowClassName}
                      >
                        <TableCell>
                          <div className="flex items-center">
                            <div>
                              <div className="font-medium">{time.formatted}</div>
                              <div className="text-xs text-muted-foreground">{time.relative}</div>
                            </div>
                            
                          </div>
                        </TableCell>
                        <TableCell>{log.agent_type || 'Unknown'}</TableCell>
                        <TableCell>{log.inputType}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end">
                            <Clock className="h-3 w-3 mr-1 text-muted-foreground" />
                            {typeof log.duration === 'number'
                              ? <span>{log.duration.toFixed(2)}s</span>
                              : <span className="text-muted-foreground">not recorded</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end">
                            <FileText className="h-3 w-3 mr-1 text-muted-foreground" />
                            {typeof log.tokenUsage === 'number'
                              ? <span>{log.tokenUsage.toLocaleString()}</span>
                              : <span className="text-muted-foreground">not recorded</span>}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          {log.customerFeedback ? (
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 mr-1" />
                              <span>{log.customerFeedback.rating}</span>
                              {log.customerFeedback.comment && (
                                <MessageSquare className="h-4 w-4 ml-1 text-muted-foreground" />
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      {filteredLogs.length === 0 && logs.length > 0 ? (
                        <div className="text-muted-foreground">
                          No logs match your filters. 
                          <Button 
                            variant="link" 
                            onClick={() => {
                              setFilterAgentType('all');
                              setFilterStatus('all');
                              setSearchQuery('');
                            }}
                          >
                            Clear filters
                          </Button>
                        </div>
                      ) : loading ? (
                        <div className="flex justify-center items-center">
                          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No execution logs found</span>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="py-4 px-6 flex items-center justify-between border-t">
              <div className="text-sm text-muted-foreground">
                Showing {indexOfFirstLog + 1}-{Math.min(indexOfLastLog, filteredLogs.length)} of {filteredLogs.length} logs
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      // Show all pages if 5 or fewer
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      // Near beginning
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Near end
                      pageNum = totalPages - 4 + i;
                    } else {
                      // In middle
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Show log detail dialog when a log is selected */}
      <EnhancedLogDetailDialog
        log={selectedLog}
        isOpen={isDetailOpen}
        onClose={closeDetail}
        isLoading={isLogLoading}
        error={detailError}
      />
    </>
  );
};

export default EnhancedExecutionLogsTable;